import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ArrowUpRight } from 'lucide-react';
import { useContent } from '../../content';
import { useLang } from '../../i18n';
import ProjectModal from '../ProjectModal';
import HScroll from '../HScroll';

// Windows-8 "Metro" style varied tile spans, cycled across the grid.
const SPANS = [
  'col-span-2 row-span-2',      // featured / hero tile
  'col-span-2 row-span-1',
  'col-span-1 row-span-1',
  'col-span-1 row-span-1',
  'col-span-2 row-span-1',
  'col-span-1 row-span-1',
  'col-span-1 row-span-1',
  'col-span-2 row-span-1'
];

const ProjectTile = ({ p, onOpen, t, span, big }) => (
  <button
    data-testid={`project-tile-${p.no}`}
    onClick={() => onOpen(p)}
    className={`group relative block w-full h-full overflow-hidden border border-white/10 bg-navy/40 transition-all duration-300 hover:border-ember/60 hover:z-10 hover:shadow-[0_16px_40px_rgba(0,0,0,0.5)] ${span}`}
  >
    <img
      src={p.img}
      alt={p.title}
      draggable="false"
      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/45 to-navy/5 group-hover:from-navy/95 transition-all duration-300" />

    <div className="absolute top-3 left-3 right-3 flex items-center justify-between">
      <span className="label-mono text-white/90 drop-shadow text-[10px] truncate max-w-[80%]">{p.no} · {p.region}</span>
      {p.featured && (
        <span className="label-mono text-ember px-2 py-0.5 rounded-full border border-ember/40 bg-navy/50 backdrop-blur-sm text-[9px] shrink-0">{t('proj.featured')}</span>
      )}
    </div>

    <div className="absolute inset-x-0 bottom-0 p-4 md:p-5">
      <h3 className={`font-display font-medium text-white leading-snug line-clamp-2 ${big ? 'text-2xl md:text-3xl' : 'text-sm md:text-lg'}`}>
        {p.title}
      </h3>
      <p className="mt-1.5 text-white/60 font-body text-xs line-clamp-1">{p.scope}</p>
      <div className="mt-3 flex items-center gap-2 opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-ember px-3 py-1.5 text-navy font-body font-semibold text-xs">
          <Play size={12} className="fill-navy" /> {t('proj.viewCaseFile')}
        </span>
        <span className="label-mono text-white/50 text-[10px]">{p.year}</span>
      </div>
    </div>

    <ArrowUpRight size={16} className="absolute top-3 right-3 text-white/0 group-hover:text-ember transition-all" />
  </button>
);

const Projects = ({ initialSlug }) => {
  const { projects } = useContent();
  const { t } = useLang();
  const [active, setActive] = useState(null);
  const [spotIdx, setSpotIdx] = useState(0);

  const findBySlug = useCallback((slug) => projects.items.find((p) => p.slug === slug) || null, [projects.items]);

  const openProject = useCallback((p) => {
    setActive(p);
    window.history.pushState({ projectSlug: p.slug }, '', `/projects/${p.slug}`);
  }, []);

  const closeProject = useCallback(() => {
    setActive(null);
    if (window.location.pathname.startsWith('/projects/')) {
      window.history.pushState({}, '', '/');
    }
  }, []);

  useEffect(() => {
    if (initialSlug) {
      const p = findBySlug(initialSlug);
      if (p) setActive(p);
    }
  }, [initialSlug, findBySlug]);

  useEffect(() => {
    const onPop = () => {
      const m = window.location.pathname.match(/^\/projects\/(.+)$/);
      setActive(m ? findBySlug(m[1]) : null);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [findBySlug]);

  // Auto-rotate the spotlight every 7s (paused while a case file is open).
  useEffect(() => {
    if (active) return;
    const total = projects.items.length;
    if (total <= 1) return;
    const id = setInterval(() => setSpotIdx((i) => (i + 1) % total), 7000);
    return () => clearInterval(id);
  }, [active, projects.items.length]);

  const featured = projects.items[spotIdx % projects.items.length] || projects.items[0];
  const rest = projects.items.filter((p) => p !== featured);

  return (
    <div>
      {/* Intro */}
      <div className="max-w-3xl mb-8 md:mb-10">
        <div className="label-mono text-ember mb-6">[{projects.index}] · {projects.label}</div>
        <h2 className="font-display font-light text-white text-3xl md:text-4xl lg:text-5xl leading-[1.1] tracking-tight">
          {projects.headingLead}
          <span className="text-white/45">{projects.headingMuted}</span>
        </h2>
        <p className="mt-8 text-white/70 font-body text-base leading-relaxed">{projects.body}</p>
      </div>

      {/* Cinematic spotlight (Netflix-style hero, auto-rotating) */}
      {featured && (
        <button
          data-testid="project-spotlight"
          onClick={() => openProject(featured)}
          className="group relative block w-full overflow-hidden rounded-2xl border border-white/10 h-[380px] sm:h-[460px] lg:h-[540px] mb-6 text-left"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={featured.slug}
              className="absolute inset-0"
              initial={{ opacity: 0, scale: 1.02 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease: 'easeInOut' }}
            >
              <img
                src={featured.img}
                alt={featured.title}
                className="absolute inset-0 w-full h-full object-cover scale-105 transition-transform duration-[1200ms] group-hover:scale-110"
                draggable="false"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/20 to-navy/40" />

              <div className="absolute inset-0 flex items-end sm:items-center">
                <div className="p-6 sm:p-10 lg:p-14 max-w-2xl">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="label-mono text-ember">{projects.label}</span>
                    <span className="label-mono text-ember px-2.5 py-1 rounded-full border border-ember/40 bg-navy/40 backdrop-blur-sm text-[9px]">{t('proj.featured')}</span>
                  </div>
                  <h3 className="font-display font-light text-white text-3xl sm:text-4xl lg:text-6xl leading-[1.04] tracking-tight">
                    {featured.title}
                  </h3>
                  <p className="mt-4 sm:mt-5 text-white/75 font-body text-sm sm:text-base leading-relaxed max-w-xl">{featured.scope}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 label-mono text-white/55">
                    {featured.year && <span>{featured.year}</span>}
                    {featured.region && <span>· {featured.region}</span>}
                    {featured.metric && <span>· {featured.metric} {featured.metricLabel}</span>}
                  </div>
                  <div className="mt-7 flex items-center gap-3">
                    <span className="inline-flex items-center gap-2.5 rounded-full bg-ember px-7 py-3.5 text-navy font-body font-semibold text-sm transition-transform group-hover:scale-[1.03]">
                      <Play size={16} className="fill-navy" /> {t('proj.viewCaseFile')}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </button>
      )}

      {/* Spotlight progress dots */}
      {projects.items.length > 1 && (
        <div className="flex items-center gap-2 mb-10" data-testid="spotlight-dots">
          {projects.items.map((p, i) => (
            <button
              key={p.slug}
              aria-label={`Show ${p.title}`}
              onClick={() => setSpotIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === spotIdx ? 'w-7 bg-ember' : 'w-2.5 bg-white/25 hover:bg-white/50'}`}
            />
          ))}
        </div>
      )}

      {/* Windows-8 Metro tile grid — remaining projects */}
      <div
        data-testid="projects-gallery"
        className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] sm:auto-rows-[180px] lg:auto-rows-[190px] gap-3"
        style={{ gridAutoFlow: 'dense' }}
      >
        {rest.map((p, i) => (
          <ProjectTile
            key={p.no}
            p={p}
            onOpen={openProject}
            t={t}
            span={SPANS[(i % (SPANS.length - 1)) + 1]}
            big={false}
          />
        ))}
      </div>

      <ProjectModal project={active} onClose={closeProject} />
    </div>
  );
};

export default Projects;
